import { useCallback, useEffect, useRef, useState } from 'react'
import { runInAction } from 'mobx'
import { toastError } from '../../../../FormComponents/Toasts'
import {
  OVERVIEW_SCOPE_KEYS,
  REELS_LIKE_TABLE_KEYS,
  BUYOUT_NOTES_DEBOUNCE_MS,
  BUYOUT_SAVE_DEBOUNCE_MS,
  REELS_LIKE_SPLIT_DEBOUNCE_MS,
  REELS_LIKE_SPLIT_SAVE_COLS,
  SCOPE_KEY_SYNC_CONFIG,
  channelColumnForAuthProvider,
  isOtherOverviewChannelKey,
  isOtherScopeKey,
  normalizeAuthProvider,
  overviewScopeKeysForProvider,
  providerForOverviewChannelKey,
  providerForOverviewScopeKey,
} from './BudgetDetailsPanelConfig'
import { parseGermanBudgetNumber, parsePercentInput, parseScopeCountToInt } from './BudgetDetailsPanelNumberUtils'
import {
  actionsForReelsTableRow,
  buyoutRowFromInfluencer,
  customFieldBudgetRowForInfluencer,
  displayValueFromCustomFieldRecord,
  influencerActionsForOverallKosten,
  normalizedCustomFields,
  overallKostenRowFromInfluencer,
  reelsLikeEmptyRowForInfluencer,
  reelsLikeRowFromInfluencerActions,
  reelsLikeRowsForTableKey,
  upsertCampaignCustomFieldValueInScenario,
} from './BudgetDetailsPanelRowBuilders'
import {
  persistOverallKostenMargins,
  persistBuyoutFieldsForInfluencer,
  persistReelsLikeSplitAcrossActions,
  saveAuthenticationReach,
  saveCampaignCustomFieldValueRequest,
  saveCostItemFieldValueRequest,
  saveManualAuthenticationForInfluencer,
  saveOtherActionGroupReach,
  saveOtherChannelLabelForInfluencer,
  syncOverviewScopeColumnToScenario,
} from './BudgetDetailsPanelPersistence'

function rowSlotNumber(row) {
  return Number(row?.slotNumber) || 1
}

function mergePendingWelcheBuyoutsRow(row, pendingWelcheBuyoutsEditsRef) {
  if (!row?.influencerId) return row
  const pending = pendingWelcheBuyoutsEditsRef?.current
  if (!pending) return row
  const key = `${row.influencerId}:${rowSlotNumber(row)}`
  if (!(key in pending)) return row
  return { ...row, welcheBuyouts: pending[key] }
}

export default function useBudgetCellHandlers({
  campaign,
  campaignScenario,
  influencers,
  authentications,
  setOverviewRows,
  setReelsLikeRowsByKey,
  setOverallKostenRows,
  setBuyoutRows,
  setCustomFieldBudgetRows,
  refs,
  pendingOverviewScopeEdits,
  pendingWelcheBuyoutsEdits,
}) {
  const overviewScopeTimersRef = useRef({})
  const overviewScopeSyncsRef = useRef({})
  const channelAuthTimersRef = useRef({})
  const reelsLikePersistTimersRef = useRef({})
  const buyoutPersistTimersRef = useRef({})
  const [overviewScopeSyncingCells, setOverviewScopeSyncingCells] = useState({})
  const [channelAuthSyncingCells, setChannelAuthSyncingCells] = useState({})

  useEffect(
    () => () => {
      Object.values(overviewScopeTimersRef.current).forEach(t => clearTimeout(t))
      Object.values(channelAuthTimersRef.current).forEach(t => clearTimeout(t))
    },
    []
  )

  useEffect(
    () => () => {
      Object.values(reelsLikePersistTimersRef.current).forEach(t => clearTimeout(t))
      Object.values(buyoutPersistTimersRef.current).forEach(t => clearTimeout(t))
    },
    []
  )

  const setOverviewScopeCellSyncing = useCallback((syncKey, isSyncing) => {
    setOverviewScopeSyncingCells(prev => {
      if (isSyncing) return { ...prev, [syncKey]: true }
      if (!prev[syncKey]) return prev
      const next = { ...prev }
      delete next[syncKey]
      return next
    })
  }, [])

  const setChannelAuthCellSyncing = useCallback((syncKey, isSyncing) => {
    setChannelAuthSyncingCells(prev => {
      if (isSyncing) return { ...prev, [syncKey]: true }
      if (!prev[syncKey]) return prev
      const next = { ...prev }
      delete next[syncKey]
      return next
    })
  }, [])

  const upsertAuthenticationInInfluencer = useCallback((influencer, authentication) => {
    if (!influencer || !authentication?.id) return
    runInAction(() => {
      const auths = influencer.authentications || []
      const idx = auths.findIndex(auth => Number(auth.id) === Number(authentication.id))
      if (idx >= 0) {
        auths[idx] = { ...auths[idx], ...authentication }
      } else {
        influencer.authentications = [...auths, authentication]
      }
    })
  }, [])

  // Channels are influencer-level (one real Authentication shared by every duplicate row), so
  // unlocking scope cells after linking a channel applies to all of the influencer's rows.
  const enableProviderScopeCellsForInfluencer = useCallback((influencerId, provider, authentication) => {
    const scopeKeys = overviewScopeKeysForProvider(provider)
    setOverviewRows(prev =>
      prev.map(row => {
        if (Number(row?.influencerId) !== Number(influencerId)) return row
        const disabledCells = { ...(row._disabledCells || {}) }
        scopeKeys.forEach(scopeKey => {
          delete disabledCells[scopeKey]
        })
        return {
          ...row,
          _authenticationsByProvider: {
            ...(row._authenticationsByProvider || {}),
            [normalizeAuthProvider(provider)]: authentication,
          },
          _disabledCells: disabledCells,
        }
      })
    )
  }, [setOverviewRows])

  const persistOtherChannelForRow = useCallback(
    async row => {
      const label = String(row?.otherChannel || '').trim()
      if (!row?.influencerId) return null
      const slotNumber = rowSlotNumber(row)
      const syncKey = `other-channel-${row.influencerId}-${slotNumber}`
      setChannelAuthCellSyncing(syncKey, true)
      try {
        const cs = refs.campaignScenarioRef.current
        return await saveOtherChannelLabelForInfluencer({
          campaignId: campaign.id,
          scenarioId: cs.id,
          campaignScenario: cs,
          influencerId: row.influencerId,
          slotNumber,
          label,
        })
      } finally {
        setChannelAuthCellSyncing(syncKey, false)
      }
    },
    [campaign?.id, refs, setChannelAuthCellSyncing]
  )

  const persistManualAuthenticationForRow = useCallback(
    async (row, provider) => {
      const providerKey = normalizeAuthProvider(provider)
      const channelKey = channelColumnForAuthProvider(providerKey)
      const username = channelKey ? String(row?.[channelKey] || '').trim() : ''
      if (!username) return null
      const influencer = influencers?.find(i => Number(i.id) === Number(row?.influencerId))
      if (!influencer) return null
      const syncKey = `channel-auth-${providerKey}-${influencer.id}`
      setChannelAuthCellSyncing(syncKey, true)
      try {
        const saved = await saveManualAuthenticationForInfluencer({
          campaignId: campaign.id,
          influencer,
          provider: providerKey,
          username,
        })
        upsertAuthenticationInInfluencer(influencer, saved)
        enableProviderScopeCellsForInfluencer(influencer.id, providerKey, saved)
        return saved
      } finally {
        setChannelAuthCellSyncing(syncKey, false)
      }
    },
    [campaign?.id, enableProviderScopeCellsForInfluencer, influencers, setChannelAuthCellSyncing, upsertAuthenticationInInfluencer]
  )

  const runOverviewScopeSync = useCallback(
    async ({ syncKey, rowIndex, influencerId, slotNumber, colKey }) => {
      const syncState = overviewScopeSyncsRef.current[syncKey]
      if (!syncState || syncState.running) return

      syncState.running = true
      setOverviewScopeCellSyncing(syncKey, true)

      try {
        while (syncState.nextRawValue !== undefined) {
          const rawValue = syncState.nextRawValue
          syncState.nextRawValue = undefined

          const cs = refs.campaignScenarioRef.current
          const currentRows = refs.overviewRowsRef.current
          const currentRow = influencerId
            ? currentRows.find(r => Number(r?.influencerId) === Number(influencerId) && rowSlotNumber(r) === slotNumber)
            : currentRows[rowIndex]
          if (!currentRow?.influencerId) break

          const target = parseScopeCountToInt(rawValue)
          const rowSlot = rowSlotNumber(currentRow)
          try {
            if (isOtherScopeKey(colKey)) {
              const channelLabel = String(currentRow.otherChannel || '').trim()
              if (channelLabel) {
                await persistOtherChannelForRow(currentRow)
              }
            } else {
              const providerKey = providerForOverviewScopeKey(colKey)
              if (providerKey) {
                const infl = influencers?.find(i => Number(i.id) === Number(currentRow.influencerId))
                const hasProviderAuth = (infl?.authentications || []).some(
                  auth => normalizeAuthProvider(auth.provider) === providerKey
                )
                const channelKey = channelColumnForAuthProvider(providerKey)
                if (!hasProviderAuth && channelKey && String(currentRow[channelKey] || '').trim()) {
                  await persistManualAuthenticationForRow(currentRow, providerKey)
                }
              }
            }
            await syncOverviewScopeColumnToScenario({
              campaignId: campaign.id,
              scenarioId: cs.id,
              campaignScenario: cs,
              influencers,
              influencerId: currentRow.influencerId,
              slotNumber: rowSlot,
              colKey,
              targetCount: target,
              currencySymbol: campaign?.currency_symbol,
            })
            pendingOverviewScopeEdits?.clearPendingOverviewScopeEdit(currentRow.influencerId, rowSlot, colKey, rawValue)
          } catch (err) {
            const msg = err?.data?.error ?? err?.message ?? 'Failed to save scope'
            toastError(typeof msg === 'string' ? msg : 'Failed to save scope')
            pendingOverviewScopeEdits?.clearPendingOverviewScopeEdit(currentRow.influencerId, rowSlot, colKey, rawValue)
          }
        }
      } finally {
        syncState.running = false
        setOverviewScopeCellSyncing(syncKey, false)
        if (syncState.nextRawValue !== undefined) {
          runOverviewScopeSync({ syncKey, rowIndex, influencerId, slotNumber, colKey })
        } else if (syncState.scheduledRawValue === undefined) {
          delete overviewScopeSyncsRef.current[syncKey]
        }
      }
    },
    [
      campaign?.id,
      campaign?.currency_symbol,
      influencers,
      pendingOverviewScopeEdits,
      persistManualAuthenticationForRow,
      persistOtherChannelForRow,
      refs,
      setOverviewScopeCellSyncing,
    ]
  )

  const handleOverviewCellChange = useCallback(
    (rowIndex, colKey, value) => {
      setOverviewRows(prev => {
        const next = [...prev]
        next[rowIndex] = { ...next[rowIndex], [colKey]: value }
        return next
      })

      const providerKey = providerForOverviewChannelKey(colKey)
      if (isOtherOverviewChannelKey(colKey)) {
        const row = refs.overviewRowsRef.current[rowIndex]
        if (!row?.influencerId) return
        const timerKey = `other-channel-${row.influencerId}-${rowSlotNumber(row)}`
        clearTimeout(channelAuthTimersRef.current[timerKey])
        channelAuthTimersRef.current[timerKey] = setTimeout(async () => {
          try {
            await persistOtherChannelForRow({ ...row, [colKey]: value })
          } catch (err) {
            const msg = err?.data?.error ?? err?.message ?? 'Failed to save other channel'
            toastError(typeof msg === 'string' ? msg : 'Failed to save other channel')
          } finally {
            delete channelAuthTimersRef.current[timerKey]
          }
        }, REELS_LIKE_SPLIT_DEBOUNCE_MS)
        return
      }

      if (providerKey) {
        const row = refs.overviewRowsRef.current[rowIndex]
        if (!row?.influencerId) return
        const timerKey = `channel-auth-${providerKey}-${row.influencerId}`
        clearTimeout(channelAuthTimersRef.current[timerKey])
        channelAuthTimersRef.current[timerKey] = setTimeout(async () => {
          try {
            await persistManualAuthenticationForRow({ ...row, [colKey]: value }, providerKey)
          } catch (err) {
            const msg = err?.data?.error ?? err?.message ?? 'Failed to save channel'
            toastError(typeof msg === 'string' ? msg : 'Failed to save channel')
          } finally {
            delete channelAuthTimersRef.current[timerKey]
          }
        }, REELS_LIKE_SPLIT_DEBOUNCE_MS)
        return
      }

      if (!OVERVIEW_SCOPE_KEYS.includes(colKey)) return
      const scopeCfg = SCOPE_KEY_SYNC_CONFIG[colKey]
      if (!scopeCfg) return
      if (!campaign?.id || !campaignScenario?.id) return

      const row = refs.overviewRowsRef.current[rowIndex]
      const influencerId = row?.influencerId
      const slotNumber = rowSlotNumber(row)
      if (influencerId) {
        pendingOverviewScopeEdits?.setPendingOverviewScopeEdit(influencerId, slotNumber, colKey, value)
      }
      const timerKey = `overview-scope-${influencerId ?? rowIndex}-${slotNumber}-${colKey}`
      const syncKey = timerKey
      if (!overviewScopeSyncsRef.current[syncKey]) {
        overviewScopeSyncsRef.current[syncKey] = {
          running: false,
          nextRawValue: undefined,
          scheduledRawValue: undefined,
        }
      }
      overviewScopeSyncsRef.current[syncKey].scheduledRawValue = value
      clearTimeout(overviewScopeTimersRef.current[timerKey])
      overviewScopeTimersRef.current[timerKey] = setTimeout(async () => {
        if (!overviewScopeSyncsRef.current[syncKey]) {
          overviewScopeSyncsRef.current[syncKey] = {
            running: false,
            nextRawValue: undefined,
            scheduledRawValue: undefined,
          }
        }
        overviewScopeSyncsRef.current[syncKey].nextRawValue =
          overviewScopeSyncsRef.current[syncKey].scheduledRawValue
        overviewScopeSyncsRef.current[syncKey].scheduledRawValue = undefined
        await runOverviewScopeSync({ syncKey, rowIndex, influencerId, slotNumber, colKey })
        delete overviewScopeTimersRef.current[timerKey]
      }, REELS_LIKE_SPLIT_DEBOUNCE_MS)
    },
    [
      campaign?.id,
      campaignScenario?.id,
      pendingOverviewScopeEdits,
      persistManualAuthenticationForRow,
      persistOtherChannelForRow,
      refs,
      runOverviewScopeSync,
      setOverviewRows,
    ]
  )

  const handleReelsLikeCellChange = useCallback(
    (tableKey, rowIndex, colKey, value) => {
      setReelsLikeRowsByKey(prev => ({
        ...prev,
        [tableKey]: (prev[tableKey] || []).map((row, i) => (i === rowIndex ? { ...row, [colKey]: value } : row)),
      }))

      if (colKey === 'follower') {
        if (!campaignScenario?.id) return
        const row = refs.reelsLikeRowsByKeyRef.current[tableKey]?.[rowIndex]
        if (tableKey === 'other_items') {
          if (!row?.influencerId) return
          const slotNumber = rowSlotNumber(row)
          const timerKey = `${tableKey}-${row.influencerId}-${slotNumber}-follower`
          clearTimeout(reelsLikePersistTimersRef.current[timerKey])
          reelsLikePersistTimersRef.current[timerKey] = setTimeout(async () => {
            const cs = refs.campaignScenarioRef.current
            const currentRow = refs.reelsLikeRowsByKeyRef.current[tableKey]?.[rowIndex]
            const parsedReach = Math.round(parseGermanBudgetNumber(currentRow?.follower))
            if (!Number.isFinite(parsedReach)) {
              toastError('Invalid follower count')
              delete reelsLikePersistTimersRef.current[timerKey]
              return
            }
            try {
              await saveOtherActionGroupReach({
                campaignId: campaign.id,
                scenarioId: cs.id,
                campaignScenario: cs,
                influencerId: currentRow.influencerId,
                slotNumber,
                reach: parsedReach,
              })
              const currencySym = campaign?.currency_symbol || '€'
              setReelsLikeRowsByKey(
                REELS_LIKE_TABLE_KEYS.reduce((acc, k) => {
                  acc[k] = reelsLikeRowsForTableKey(cs, k, influencers, currencySym, campaign, authentications)
                  return acc
                }, {})
              )
            } catch (err) {
              const msg = err?.data?.error ?? err?.message ?? 'Save failed'
              toastError(typeof msg === 'string' ? msg : 'Save failed')
            } finally {
              delete reelsLikePersistTimersRef.current[timerKey]
            }
          }, REELS_LIKE_SPLIT_DEBOUNCE_MS)
          return
        }
        const authenticationId = row?.authenticationId
        if (!authenticationId) return
        const timerKey = `${tableKey}-${authenticationId}-follower`
        clearTimeout(reelsLikePersistTimersRef.current[timerKey])
        reelsLikePersistTimersRef.current[timerKey] = setTimeout(async () => {
          const cs = refs.campaignScenarioRef.current
          const currentRow = refs.reelsLikeRowsByKeyRef.current[tableKey]?.[rowIndex]
          const parsedReach = Math.round(parseGermanBudgetNumber(currentRow?.follower))
          if (!Number.isFinite(parsedReach)) {
            toastError('Invalid follower count')
            delete reelsLikePersistTimersRef.current[timerKey]
            return
          }
          try {
            const updatedAuth = await saveAuthenticationReach(authenticationId, parsedReach)
            runInAction(() => {
              influencers?.forEach(influencer => {
                const auth = (influencer.authentications || []).find(a => Number(a.id) === Number(authenticationId))
                if (auth) auth.reach = updatedAuth.reach
              })
              const actions = cs?.actions || []
              actions.forEach(action => {
                if (Number(action.authentication_id) === Number(authenticationId)) action.reach = updatedAuth.reach
              })
            })
            const currencySym = campaign?.currency_symbol || '€'
            setReelsLikeRowsByKey(
              REELS_LIKE_TABLE_KEYS.reduce((acc, k) => {
                acc[k] = reelsLikeRowsForTableKey(cs, k, influencers, currencySym, campaign, authentications)
                return acc
              }, {})
            )
          } catch (err) {
            const msg = err?.data?.error ?? err?.message ?? 'Save failed'
            toastError(typeof msg === 'string' ? msg : 'Save failed')
          } finally {
            delete reelsLikePersistTimersRef.current[timerKey]
          }
        }, REELS_LIKE_SPLIT_DEBOUNCE_MS)
        return
      }

      if (!REELS_LIKE_SPLIT_SAVE_COLS.includes(colKey)) return
      if (!campaign?.id || !campaignScenario?.id) return

      const row = refs.reelsLikeRowsByKeyRef.current[tableKey]?.[rowIndex]
      const slotNumber = rowSlotNumber(row)
      const timerKey = `${tableKey}-${row?.influencerId ?? rowIndex}-${slotNumber}-${colKey}`
      clearTimeout(reelsLikePersistTimersRef.current[timerKey])
      reelsLikePersistTimersRef.current[timerKey] = setTimeout(async () => {
        const cs = refs.campaignScenarioRef.current
        const currentRow = refs.reelsLikeRowsByKeyRef.current[tableKey]?.[rowIndex]
        if (!currentRow?.influencerId) return
        const currencySym = campaign?.currency_symbol || '€'
        try {
          await persistReelsLikeSplitAcrossActions({
            campaignId: campaign.id,
            scenarioId: cs.id,
            campaignScenario: cs,
            tableKey,
            influencerId: currentRow.influencerId,
            slotNumber,
            colKey,
            rawCellValue: currentRow[colKey],
          })
          const inf = influencers?.find(i => Number(i.id) === Number(currentRow.influencerId))
          if (!inf) return
          const acts = actionsForReelsTableRow(cs, tableKey, currentRow.influencerId, slotNumber)
          const newRow = acts.length
            ? reelsLikeRowFromInfluencerActions(inf, acts, currencySym, slotNumber)
            : reelsLikeEmptyRowForInfluencer(inf, currencySym, slotNumber)
          setReelsLikeRowsByKey(prev => ({
            ...prev,
            [tableKey]: (prev[tableKey] || []).map((r, i) => (i === rowIndex ? newRow : r)),
          }))
        } catch (err) {
          const msg = err?.data?.error ?? err?.message ?? 'Save failed'
          toastError(typeof msg === 'string' ? msg : 'Save failed')
          setReelsLikeRowsByKey(prev => ({
            ...prev,
            [tableKey]: reelsLikeRowsForTableKey(cs, tableKey, influencers, currencySym, campaign, authentications),
          }))
        } finally {
          delete reelsLikePersistTimersRef.current[timerKey]
        }
      }, REELS_LIKE_SPLIT_DEBOUNCE_MS)
    },
    [campaign, campaignScenario?.id, influencers, authentications, refs, setReelsLikeRowsByKey]
  )

  const handleOverallKostenCellChange = useCallback(
    async (rowIndex, colKey, value) => {
      const previousRow = refs.overallKostenRowsRef.current[rowIndex]
      setOverallKostenRows(prev => {
        const next = [...prev]
        next[rowIndex] = { ...next[rowIndex], [colKey]: value }
        return next
      })

      if (!['margeBuyouts', 'margeMedia'].includes(colKey)) return
      if (!campaign?.id || !campaignScenario?.id) return
      if (!previousRow?.influencerId) return

      const slotNumber = rowSlotNumber(previousRow)
      const parsed = parsePercentInput(value)
      if (!Number.isFinite(parsed)) {
        const sym = campaign?.currency_symbol || '€'
        const inf = influencers?.find(i => Number(i.id) === Number(previousRow.influencerId))
        if (!inf) return
        setOverallKostenRows(prev => {
          const next = [...prev]
          next[rowIndex] = overallKostenRowFromInfluencer(inf, campaignScenario, sym, campaign, slotNumber)
          return next
        })
        toastError('Invalid percentage')
        return
      }

      const influencerId = previousRow.influencerId
      const actions = influencerActionsForOverallKosten(campaignScenario, influencerId, slotNumber)
      if (!actions.length) return

      try {
        await persistOverallKostenMargins({
          campaignId: campaign.id,
          scenarioId: campaignScenario.id,
          campaignScenario,
          actions,
          colKey,
          parsed,
        })

        const sym = campaign?.currency_symbol || '€'
        const inf = influencers?.find(i => Number(i.id) === Number(influencerId))
        if (!inf) return
        setOverallKostenRows(prev => {
          const next = [...prev]
          next[rowIndex] = overallKostenRowFromInfluencer(inf, campaignScenario, sym, campaign, slotNumber)
          return next
        })
      } catch (err) {
        setOverallKostenRows(prev => {
          const next = [...prev]
          next[rowIndex] = previousRow
          return next
        })
        const msg = err?.data?.error ?? err?.message ?? 'Save failed'
        toastError(typeof msg === 'string' ? msg : 'Save failed')
      }
    },
    [campaign, campaignScenario, influencers, refs, setOverallKostenRows]
  )

  const handleBuyoutCellChange = useCallback(
    (rowIndex, colKey, value) => {
      if (!['anzahlBuyout', 'buyoutUnitPrice', 'welcheBuyouts'].includes(colKey)) return
      if (!campaign?.id || !campaignScenario?.id) return

      const previousRow = refs.buyoutRowsRef.current[rowIndex]
      if (!previousRow?.influencerId) return
      if (previousRow._noMediaActions) return

      const influencerId = previousRow.influencerId
      const slotNumber = rowSlotNumber(previousRow)
      const isNotesField = colKey === 'welcheBuyouts'

      if (isNotesField) {
        pendingWelcheBuyoutsEdits?.setPendingWelcheBuyoutsEdit(influencerId, slotNumber, value)
      }

      setBuyoutRows(prev => {
        const next = [...prev]
        next[rowIndex] = { ...next[rowIndex], [colKey]: value }
        return next
      })

      const timerKey = `buyout-${influencerId}-${slotNumber}-${colKey}`
      clearTimeout(buyoutPersistTimersRef.current[timerKey])
      buyoutPersistTimersRef.current[timerKey] = setTimeout(async () => {
        const cs = refs.campaignScenarioRef.current
        const currentRow = refs.buyoutRowsRef.current[rowIndex]
        if (!currentRow?.influencerId) return

        const sym = campaign?.currency_symbol || '€'
        const inf = influencers?.find(i => Number(i.id) === Number(currentRow.influencerId))
        const savedValue = currentRow[colKey]
        const snapshotRow = { ...currentRow }

        try {
          await persistBuyoutFieldsForInfluencer({
            campaignId: campaign.id,
            scenarioId: cs.id,
            campaignScenario: cs,
            influencerId: currentRow.influencerId,
            slotNumber,
            colKey,
            rawValue: savedValue,
          })

          if (isNotesField) {
            pendingWelcheBuyoutsEdits?.clearPendingWelcheBuyoutsEdit(currentRow.influencerId, slotNumber, savedValue)
            return
          }

          if (!inf) return
          setBuyoutRows(prev => {
            const next = [...prev]
            const refreshed = buyoutRowFromInfluencer(inf, cs, sym, slotNumber)
            next[rowIndex] = mergePendingWelcheBuyoutsRow(refreshed, refs.pendingWelcheBuyoutsEditsRef)
            return next
          })
          setOverallKostenRows(prev =>
            prev.map(overallRow => {
              if (Number(overallRow.influencerId) !== Number(currentRow.influencerId) || rowSlotNumber(overallRow) !== slotNumber) {
                return overallRow
              }
              return inf ? overallKostenRowFromInfluencer(inf, cs, sym, campaign, slotNumber) : overallRow
            })
          )
        } catch (err) {
          setBuyoutRows(prev => {
            const next = [...prev]
            next[rowIndex] = snapshotRow
            return next
          })
          const msg = err?.data?.error ?? err?.message ?? 'Save failed'
          toastError(typeof msg === 'string' ? msg : 'Save failed')
        } finally {
          delete buyoutPersistTimersRef.current[timerKey]
        }
      }, isNotesField ? BUYOUT_NOTES_DEBOUNCE_MS : BUYOUT_SAVE_DEBOUNCE_MS)
    },
    [campaign, campaignScenario, influencers, pendingWelcheBuyoutsEdits, refs, setBuyoutRows, setOverallKostenRows]
  )

  const handleCustomFieldBudgetCellChange = useCallback(
    async (rowIndex, colKey, value) => {
      if (!campaign?.id || !campaignScenario?.id) return
      if (!colKey.startsWith('customField_')) return

      const costItemAttr = colKey.endsWith('_qty') ? 'quantity' : colKey.endsWith('_price') ? 'unit_price' : null
      const fieldIdPart = costItemAttr
        ? colKey.slice('customField_'.length, colKey.lastIndexOf('_'))
        : colKey.slice('customField_'.length)
      const fields = normalizedCustomFields(campaign)
      const field = fields.find(
        f => String(f.id) === fieldIdPart || Number(f.id) === Number(fieldIdPart)
      )
      if (!field || !Number.isFinite(Number(field.id))) return

      const row = refs.customFieldBudgetRowsRef.current[rowIndex]
      if (!row?.influencerId) return
      const previousRow = { ...row }
      const slotNumber = rowSlotNumber(row)

      setCustomFieldBudgetRows(prev => {
        const next = [...prev]
        const r = prev[rowIndex]
        if (!r?.influencerId) return prev
        next[rowIndex] = { ...r, [colKey]: value }
        return next
      })

      const isNumeric = (field.fieldType || field.field_type) === 'numeric'
      const influencerId = row.influencerId

      try {
        const saved = costItemAttr
          ? await saveCostItemFieldValueRequest({
              campaignId: campaign.id,
              scenarioId: campaignScenario.id,
              campaignScenario,
              influencerId,
              slotNumber,
              field,
              attr: costItemAttr,
              rawValue: value,
            })
          : await saveCampaignCustomFieldValueRequest({
              campaignId: campaign.id,
              scenarioId: campaignScenario.id,
              campaignScenario,
              influencerId,
              slotNumber,
              field,
              rawValue: value,
            })

        runInAction(() => {
          upsertCampaignCustomFieldValueInScenario(campaignScenario, saved)
        })

        const sym = campaign?.currency_symbol || '€'
        const inf = influencers?.find(i => Number(i.id) === Number(influencerId))

        if (costItemAttr) {
          if (inf) {
            const refreshedRow = customFieldBudgetRowForInfluencer(inf, campaignScenario, fields, sym, slotNumber)
            setCustomFieldBudgetRows(prev => {
              const next = [...prev]
              if (!next[rowIndex]) return prev
              next[rowIndex] = refreshedRow
              return next
            })
          }
        } else {
          const display = displayValueFromCustomFieldRecord(field, saved)
          setCustomFieldBudgetRows(prev => {
            const next = [...prev]
            if (!next[rowIndex]) return prev
            next[rowIndex] = { ...next[rowIndex], [colKey]: display }
            return next
          })
        }

        if (isNumeric || costItemAttr) {
          setOverallKostenRows(prev =>
            prev.map(overallRow => {
              if (Number(overallRow.influencerId) !== Number(influencerId) || rowSlotNumber(overallRow) !== slotNumber) {
                return overallRow
              }
              return inf ? overallKostenRowFromInfluencer(inf, campaignScenario, sym, campaign, slotNumber) : overallRow
            })
          )
        }
      } catch (err) {
        setCustomFieldBudgetRows(prev => {
          const next = [...prev]
          next[rowIndex] = previousRow
          return next
        })
        const msg = err?.data?.error ?? err?.message ?? 'Save failed'
        toastError(typeof msg === 'string' ? msg : 'Save failed')
      }
    },
    [campaign, campaignScenario, influencers, refs, setCustomFieldBudgetRows, setOverallKostenRows]
  )

  return {
    handleOverviewCellChange,
    handleReelsLikeCellChange,
    handleOverallKostenCellChange,
    handleBuyoutCellChange,
    handleCustomFieldBudgetCellChange,
    isOverviewScopeSyncing: Object.keys(overviewScopeSyncingCells).length > 0,
    isChannelAuthSyncing: Object.keys(channelAuthSyncingCells).length > 0,
  }
}
